import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './ReviewStatusComponent.scss';
import { Toast } from 'primereact/toast';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';

const fetchReviewStatuses = () => {
  const url = `${ApiEndpoints.GetAllReviewStatus}`;
  return httpService.get(url);
};

interface ReviewStatusComponentProps {
  providerTypes: { label: string; value: string }[];
}

const ReviewStatusComponent: React.FC<ReviewStatusComponentProps> = ({ providerTypes }) => {
  // Inject custom style for dialog overlay to ensure lighter background
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('review-status-styles');
    return cleanup;
  }, []);

  const [reviewStatuses, setReviewStatuses] = useState<any[]>([]);
  const toast = React.useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.REVIEW_STATUSES);

      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.REVIEW_STATUSES,
      });

      const response = await fetchReviewStatuses();
      setReviewStatuses(response.data as any[]);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Review Statuses uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.reviewStatuses,
    onUpload: handleExcelUpload,
    title: 'Import Review Statuses from Excel',
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    fetchReviewStatuses()
      .then((response: any) => {
        setReviewStatuses(response.data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(`Failed to fetch review statuses: ${err.message}`);
        setLoading(false);
      });
  }, []);

  // Get unique provider types for dropdown (filter)
  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes
    : Array.from(new Set(reviewStatuses.map(s => s.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  // Filter review statuses by provider type and search term
  let filteredStatuses = providerTypeFilter ? reviewStatuses.filter(s => s.providerType === providerTypeFilter) : reviewStatuses;
  if (searchTerm.trim()) {
    filteredStatuses = filteredStatuses.filter(s =>
      s.statusName && s.statusName.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  }

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading review statuses" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Review Status" icon="pi pi-plus" onClick={() => {
            setSelectedStatus({ statusId: 0, statusName: '', statusCode: '', sequence: 0, providerType: '', isActive: true });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="review-status-search-anim">
          <input
            type="text"
            placeholder="Search by Status Name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => setProviderTypeFilter(e.target.value)}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#f0f4f8' }}>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 80 }}>#</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 200 }}>Status Name</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 150 }}>Status Code</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Sequence</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 150 }}>Provider Type</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Is Active</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredStatuses.length === 0 ? (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16 }}>No review statuses found.</td></tr>
          ) : (
            filteredStatuses.map((status: any, idx: number) => (
              <tr
                key={status.statusId || idx}
                style={{ background: idx % 2 === 0 ? '#fff' : '#f6f8fa' }}
              >
                <td style={{ padding: 8, border: '1px solid #eee' }}>{idx + 1}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{status.statusName || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{status.statusCode || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{status.sequence ?? '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{status.providerType || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{status.isActive ? 'Yes' : 'No'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>
                  <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => { setSelectedStatus({ ...status }); setIsAddMode(false); setEditDialogVisible(true); }} />
                  <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => { setStatusToDelete(status); setDeleteDialogVisible(true); }} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div style={{ marginTop: 16, textAlign: 'right', color: '#888' }}>
        {/* Pagination placeholder */}
      </div>
      <Dialog
        header={isAddMode ? "Add Review Status" : `Edit Review Status${selectedStatus && selectedStatus.statusName ? ` - ${selectedStatus.statusName}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '500px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.reviewStatus, selectedStatus);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.reviewStatus, selectedStatus);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddReviewStatus, payload);
                  const response = await fetchReviewStatuses();
                  setReviewStatuses(response.data as any[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Review Status added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedStatus) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateReviewStatus}`, payload);
                  const response = await fetchReviewStatuses();
                  setReviewStatuses(response.data as any[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Review Status updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <ReviewStatusEditForm
          editStatus={selectedStatus}
          setEditStatus={setSelectedStatus}
          providerTypes={providerTypes}
          isEditing={!isAddMode}
        />
      </Dialog>
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={() => {
              setReviewStatuses(reviewStatuses.filter(s => s.statusId !== (statusToDelete && statusToDelete.statusId)));
              setDeleteDialogVisible(false);
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{statusToDelete && statusToDelete.statusName ? `: ${statusToDelete.statusName}` : ''}?
        </div>
      </Dialog>
    </div>
  );
}

interface ReviewStatusEditFormProps {
  editStatus: any;
  setEditStatus: (status: any) => void;
  providerTypes: { label: string; value: string }[];
  isEditing: boolean;
}

const ReviewStatusEditForm: React.FC<ReviewStatusEditFormProps> = ({ editStatus, setEditStatus, providerTypes, isEditing }) => {
  return (
    <DataManagementForm
      fields={dataManagementFormConfig.reviewStatus}
      values={editStatus}
      onChange={setEditStatus}
      options={{ providerType: providerTypes }}
      isEditing={isEditing}
    />
  );
};

export default ReviewStatusComponent;